import * as React from 'react'
import { useDispatch } from 'react-redux'
import Image from 'next/image'

import type {
  JobPostingEntitlement,
  JobPostingUpgradeEntitlement,
} from '../../../state/reducers/entitlement/entitlement'
import type { SiteAlias } from '../../../schema/webshop-package'
import type { JobPostingPackage, UpgradesPackage } from '../../../state/reducers/packages/packages'
import type { DraftStoreType } from '../../../state/reducers/draft/draft'

import navigateToPage from '../../../util/navigate-to-page/navigateToPage'
import { saveDraft } from '../../../service/util/localStorage/localStorage'
import { siteAliasMap } from '../../../schema/mappers/site'
import getJobPostingPackageByEntitlement from '../../../state/selectors/packages/getJobPostingPackageByEntitlement'
import { setProductOfDraft, setUpgradesOfDraft } from '../../../state/actions/draft/draft'
import { resetCart, setPackages } from '../../../state/actions/cart/cart'
import getJobPostingsBySites from '../../../state/selectors/packages/getJobPostingsBySites'
import { LogoContainer, Form } from './SuggestProducts.style'
import {
  getEntitlementByJobPostingPackage,
  getEntitlementByJobPostingProduct,
} from '../../../state/selectors/entitlement/util/get-entitlement-by-suggestible/getEntitlementBySuggestible'
import ProductSelectionForm from '../../../stories/organisms/ProductSelectionForm/ProductSelectionForm'
import { Variant } from '../../../stories/molecules/ProductCard/ProductCard'
import FormSection from '../../../stories/molecules/FormSection/FormSection'
import getEntitlementsNotMatchingJobPostings from '../../../state/selectors/entitlement/util/get-entitlements-not-matching-postings/getEntitlementsNotMatchingJobPostings'
import { selectDraft } from '../../../state/selectors/draft/draft'
import { getSelectableUpgrades } from '../../../service/upgrades/getSelectableUpgrades'
import { SubmitDraftType, useSubmitDraft } from '../util/useSubmitDraft'
import { getConfig } from '../../../service/util/config/config'
import NavigationButtons from '../../../stories/molecules/NavigationButtons/NavigationButtons'
import { useAppSelector } from '../../../state/reducers/types'
import { State } from '../../../state/reducers'
import { WebshopPackage } from '../../../schema/webshop-package'
import { getIsSubmittingJob } from '../../../state/selectors/ui/ui'
import { Action, Category } from '../../../util/analytics-data-attributes/analyticsAttributes'

type SelectedProduct = JobPostingPackage | JobPostingEntitlement | UpgradesPackage

type DisplayedProduct = SelectedProduct & {
  selected: boolean
}

export const sites: SiteAlias[] = [...siteAliasMap.keys()]
export const selectSuggestiblePackages = (state: State) => state.packages.suggestiblePackages
export const selectEntitlements = (state: State) => state.entitlements

const SuggestProducts: React.FunctionComponent = () => {
  const dispatch = useDispatch()
  const submitDraft = useSubmitDraft()

  const isSubmitting = useAppSelector(getIsSubmittingJob)
  const draft = useAppSelector(selectDraft) as DraftStoreType
  const suggestiblePackages = useAppSelector(selectSuggestiblePackages)
  const entitlements = useAppSelector(selectEntitlements)

  // When a draft has been created from the product overview, a draft's product isn't a suggestible
  // package, but the 'jobPosting' from an entitlement. In order to match the draft's product with
  // other suggestible packages, we need to get the corresponding package from either the list of
  // job posting entitlements or from the list of available suggestible packages (since both
  // suggestible packages and entitlements will be shown as selectable products).
  const packageFromDraftProduct =
    getJobPostingPackageByEntitlement(suggestiblePackages, draft.product) ||
    getEntitlementByJobPostingProduct(entitlements.jobPostings, draft.product)

  const [selectedSites, setSelectedSites] = React.useState<SiteAlias[]>(draft.product.publishOn)
  const [selectedProduct, setSelectedProduct] = React.useState<SelectedProduct>(packageFromDraftProduct)
  const [selectedUpgrade, setSelectedUpgrade] = React.useState<
    UpgradesPackage | JobPostingUpgradeEntitlement | WebshopPackage | undefined
  >()

  const matchingProducts: JobPostingPackage[] = getJobPostingsBySites(selectedSites, suggestiblePackages)

  const [matchingUpgrades, setMatchingUpgrades] = React.useState<
    Array<{
      product?: UpgradesPackage
      entitlement?: JobPostingUpgradeEntitlement
    }>
  >([])

  const matchingEntitlements: JobPostingEntitlement[] = getEntitlementsNotMatchingJobPostings(
    entitlements.jobPostings,
    matchingProducts,
    selectedSites,
  )

  const hasSiteSelected = selectedSites.length > 0
  const hasMatchingUpgrades = matchingUpgrades.length > 0
  const showUpgrades = hasMatchingUpgrades && selectedProduct

  React.useEffect(() => {
    dispatch(resetCart())

    if (selectedProduct) {
      dispatch(setPackages({ packages: [selectedProduct] }))
    }
  }, [])

  React.useEffect(() => {
    if (selectedProduct) {
      setMatchingUpgrades([])
      getSelectableUpgrades({
        jobPostingDefinition:
          (selectedProduct as JobPostingPackage).jobPosting || (selectedProduct as JobPostingEntitlement).product,
        jobUpgradeDefinitions: (selectedProduct as JobPostingPackage).upgrades || [],
      }).then(response => {
        setMatchingUpgrades(response.data.selectableUpgrades)
      })
    }
  }, [selectedProduct])

  const getSelectedProductId = (): string | undefined => {
    if (!selectedProduct) return undefined

    return (selectedProduct as JobPostingPackage).productCode || (selectedProduct as JobPostingEntitlement).id
  }

  /**
   * Logic to determine which site input element needs to be disabled based on the current
   * selection of sites, because itbanen can only be selected whenever another site has been
   * selected as well.
   */
  const shouldSiteOptionBeDisabled = (site: SiteAlias): boolean => {
    const siteDisableRules = {
      nationalevacaturebank: selectedSites.includes('intermediair') && selectedSites.includes('itbanen'),
      intermediair: selectedSites.includes('nationalevacaturebank') && selectedSites.includes('itbanen'),
      itbanen:
        selectedSites.length === 0 ||
        (selectedSites.includes('nationalevacaturebank') && selectedSites.includes('intermediair')) ||
        (!selectedSites.includes('nationalevacaturebank') && !selectedSites.includes('intermediair')),
    }

    return siteDisableRules[site]
  }

  /**
   * Logic for the selection of the site options; itbanen cannot be selected on its own
   */
  const onSiteSelect = (site: SiteAlias, isChecked: boolean) => {
    if (isChecked) {
      setSelectedSites([...selectedSites, site])
    } else {
      const selected = selectedSites.filter(selectedSite => selectedSite !== site)

      if (selected.length === 1 && selected.includes('itbanen')) {
        setSelectedSites([])
      } else {
        setSelectedSites(selected)
      }
    }

    setSelectedProduct(undefined)
  }

  // istanbul ignore next
  const onSubmitForm: React.MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault()
    // Preventing condition from negatively impacting coverage; Flow requires the condition
    // but it can never occur, because the form cannot be submitted when no product has been
    // selected. Catch-22 FTW.
    // istanbul ignore next
    if (!selectedProduct) return

    // determine if a product or an upgrade has been selected for which there are no credits available
    // and thus require payment
    const productCreditMatch: JobPostingEntitlement =
      selectedProduct && getEntitlementByJobPostingPackage(entitlements.jobPostings, selectedProduct as any)

    const packagesThatRequirePayment: SelectedProduct[] = [
      productCreditMatch ? null : selectedProduct,
      (selectedUpgrade as JobPostingUpgradeEntitlement)?.product ? null : selectedUpgrade,
    ].filter(Boolean) as any

    if (productCreditMatch && packagesThatRequirePayment.length === 0) {
      const upgrades =
        selectedUpgrade && (selectedUpgrade as JobPostingUpgradeEntitlement).product
          ? [(selectedUpgrade as JobPostingUpgradeEntitlement).product]
          : []
      const product = productCreditMatch.product

      submitDraft({
        draftId: draft.id,
        product,
        upgrades,
      } as SubmitDraftType)
    } else {
      const product =
        (selectedProduct as JobPostingPackage).jobPosting || (selectedProduct as JobPostingEntitlement).product

      const upgrades = [...((selectedProduct as JobPostingPackage).upgrades || [])] // always the upgrades of a jobposting product itself + possible selected upgrades
      if (selectedUpgrade) {
        if (
          (selectedUpgrade as WebshopPackage).upgrades &&
          (selectedUpgrade as WebshopPackage).upgrades.length > 0
        ) {
          upgrades.push((selectedUpgrade as WebshopPackage).upgrades[0]) // one selected upgrade only has one upgrade
        }
        if ((selectedUpgrade as JobPostingUpgradeEntitlement).product) {
          upgrades.push((selectedUpgrade as JobPostingUpgradeEntitlement).product)
        }
      }

      dispatch(setProductOfDraft(product))
      dispatch(setUpgradesOfDraft(upgrades))
      dispatch(setPackages({ packages: packagesThatRequirePayment }))

      saveDraft({
        ...draft,
        product,
        upgrades,
      })

      navigateToPage('/vacatures/cart')
    }
  }

  const onPrevious: React.MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault()
    navigateToPage(`/vacature/${draft.id}/informatie`)
  }

  const selectedProductId = getSelectedProductId()
  const displayedProducts: DisplayedProduct[] = matchingEntitlements
    .map(item => ({ ...item, selected: selectedProductId === item.id }))
    .concat(matchingProducts.map(item => ({ ...item, selected: selectedProductId === item.productCode } as any)))

  const onSelectProduct = ({ id }: { id: string }) => {
    const product = displayedProducts.find(
      item => id === (item as WebshopPackage).productCode || id === (item as JobPostingEntitlement).id,
    )
    setSelectedProduct(product)
    setSelectedUpgrade(undefined)
  }

  const onSelectUpgrade = ({ id }: { id: string }) => {
    const upgrade = matchingUpgrades.find(item => id === item.entitlement?.id || id === item.product?.productCode)
    const newId = upgrade?.entitlement ? upgrade.entitlement?.id : upgrade?.product?.productCode
    const oldId = upgrade?.entitlement
      ? (selectedUpgrade as JobPostingUpgradeEntitlement)?.id
      : (selectedUpgrade as WebshopPackage)?.productCode
    if (selectedUpgrade && newId === oldId) {
      setSelectedUpgrade(undefined)
    } else {
      setSelectedUpgrade(upgrade?.product || upgrade?.entitlement)
    }
  }

  const products = displayedProducts.map(item => {
    const credit = getEntitlementByJobPostingPackage(entitlements.jobPostings, item as any)
    const cost = credit ? `1 credit (${credit.available} beschikbaar)` : `€ ${(item as WebshopPackage)?.price},-`
    const buttonLabel = credit ? 'gebruik credit' : 'koop credit'

    return {
      selected: item.selected,
      title: (item as WebshopPackage).name || credit?.product?.name,
      buttonLabel,
      description: (item as WebshopPackage).descriptions || [],
      cost,
      id: (item as WebshopPackage).productCode || (item as JobPostingEntitlement).id,
    }
  })

  const upgrades = matchingUpgrades.map(upgrade => {
    const credit = upgrade.entitlement
    const selectedUpgradeEntitlement = !!(
      upgrade.entitlement?.id && upgrade.entitlement.id === (selectedUpgrade as JobPostingUpgradeEntitlement)?.id
    )
    const selectedUpgradeToBuy = !!(
      (selectedUpgrade as WebshopPackage)?.productCode &&
      (selectedUpgrade as WebshopPackage).productCode === upgrade.product?.productCode
    )
    const selected = !!(selectedUpgrade && (selectedUpgradeEntitlement || selectedUpgradeToBuy))

    const cost = credit ? `1 credit (${credit.available} beschikbaar)` : `€ ${upgrade.product?.price},-`
    const buttonLabel = credit ? 'gebruik credit' : 'koop credit'

    return {
      selected,
      title: upgrade.product?.name || credit?.product?.name,
      description: upgrade.product?.descriptions || [],
      buttonLabel,
      cost,
      variant: Variant.Checkbox,
      id: upgrade.entitlement?.id || upgrade.product?.productCode,
    }
  })

  return (
    <Form className="website-form">
      <FormSection title="Selecteer de vacature site">
        <LogoContainer>
          {sites.map((site: SiteAlias) => (
            <label data-testid={`suggestProductsLabelSite-${site}`} htmlFor={`site-${site}`} key={site}>
              <input
                checked={selectedSites.includes(site)}
                data-testid={`suggestProductsInputSite-${site}`}
                disabled={shouldSiteOptionBeDisabled(site)}
                id={`site-${site}`}
                name={`site-${site}`}
                onChange={({ target: { checked } }) => onSiteSelect(site, checked)}
                type="checkbox"
              />
              <span className="visuallyHidden">{siteAliasMap.get(site)}</span>

              {site === 'nationalevacaturebank' && (
                <Image
                  src={`${getConfig().ASSET_PREFIX}/images/logo-nationalevacaturebank.svg`}
                  height={25}
                  width={125}
                  alt='nationalevacaturebank'
                />
              )}
              {site === 'intermediair' && (
                <Image src={`${getConfig().ASSET_PREFIX}/images/logo-intermediair.svg`} height={25} width={125} alt='intermediair' />
              )}
              {site === 'itbanen' && (
                <Image src={`${getConfig().ASSET_PREFIX}/images/logo-itbanen2.svg`} height={25} width={125} alt='itbanen'/>
              )}
            </label>
          ))}
        </LogoContainer>
      </FormSection>

      {hasSiteSelected && (
        <ProductSelectionForm title="Selecteer het type vacature" onSelect={onSelectProduct} products={products} />
      )}
      {showUpgrades && (
        <ProductSelectionForm title="Selecteer het type upgrade" onSelect={onSelectUpgrade} products={upgrades} />
      )}

      <NavigationButtons
        next={{
          onClick: onSubmitForm,
          analytics: {
            action: Action.jpfFurtherJobPosting,
            category: Category.jobPostingFlow
          },
          disabled: isSubmitting
        }}
        previous={{
          onClick: onPrevious,
          analytics: {
            action: Action.jpfCancelJobPosting,
            category: Category.jobPostingFlow
          },
        }}
      />
    </Form>
  )
}

export default SuggestProducts
